'use client';

/**
 * Seq telemetry for the auth-session lifecycle.
 *
 * The forced sign-out used to emit nothing at all, which is why a defect that
 * logged the user out roughly every other night ran for three weeks before
 * anyone could name it (see `planning/auth-session-expiry/plan.md` §2.3). Every
 * refresh decision and every forced sign-out now leaves a trace.
 *
 * Delivery rides the shared frontend transport, which is a no-op outside a
 * Seq-enabled development build — production visibility for the terminal
 * sign-out is a separate, un-taken decision, NOT something this module quietly
 * provides.
 */

import { LogLevel, type LogProps } from '@cabal/shared/util/seq-log';
import {
  emitSeqLog,
  flushSeqLogBeforeNavigation,
} from '@/hooks/telemetry/seq-log-transport';

const COMPONENT = 'AuthSession';

export enum AuthSessionEvent {
  /** A request found the token inside the expiry margin and refreshed ahead of it. */
  TokenRefreshPreemptive = 'auth.token.refresh_preemptive',
  /** The consecutive-401 breaker tripped and forced a recovery refresh. */
  TokenRefreshForced = 'auth.token.refresh_forced',
  /** Result of a single refresh attempt, whoever asked for it. */
  TokenRefreshOutcome = 'auth.token.refresh_outcome',
  /** Recovery gave up: the refresh token is definitively dead. */
  SessionSignedOutForced = 'auth.session.signed_out_forced',
  /**
   * A 401 reached the breaker.
   *
   * This is the frontend's own record of 401 traffic and it exists because the
   * backend has none: 401s are deliberately excluded from BL error logging
   * (`api/server.ts`, `api/plugins/trace.ts`), and `auth.invalid_token` only
   * fires when a bearer token fails verification — a request that carried no
   * token at all is invisible there. Diagnosing a sign-out from BL logs alone
   * is therefore impossible; this closes that blind spot.
   */
  UnauthorizedObserved = 'auth.api.unauthorized',
}

export function logAuthSessionEvent(input: {
  event: AuthSessionEvent;
  props: LogProps;
  level?: LogLevel;
}): void {
  emitSeqLog({
    msg: input.event,
    component: COMPONENT,
    props: input.props,
    level: input.level ?? LogLevel.Info,
  });
}

/**
 * Emit and drain, for events whose caller is about to navigate the document
 * away and would otherwise lose them.
 *
 * The buffer is drained BEFORE the event is added, not just after: buffering an
 * event that crosses the size cap triggers an immediate drain through the
 * ordinary deferred `fetch`, which the pending navigation can cancel. Emptying
 * first guarantees this event is the one that leaves via the beacon transport.
 */
export function logAuthSessionEventAndFlush(input: {
  event: AuthSessionEvent;
  props: LogProps;
  level?: LogLevel;
}): void {
  flushSeqLogBeforeNavigation();
  logAuthSessionEvent(input);
  flushSeqLogBeforeNavigation();
}
